import type { ConsoleKind } from "./consoleNav";
import type { SubscriptionStatus } from "../types";
import { holdsBilling, isAdminTenantType } from "./tenantAccess";

// ENG-3454: the console's one signup entry point, `?source=…&plan=…`, shared by the Workspace
// Marketplace and the website's pricing buttons (ENG-3579). `source` is attribution only; the plan
// never comes from it.

export type SignupPlan = "individual" | "family" | "business";

export interface SignupIntent {
  source: string | null;
  plan: SignupPlan | null;
}

/** Plans that create an organization, in the backend's own words (`POST /tenant`). */
export type OrgPlan = Exclude<SignupPlan, "individual">;

const PLAN_ALIASES: Record<string, SignupPlan> = {
  individual: "individual",
  me: "individual",
  family: "family",
  business: "business",
  organization: "business",
};

/** The Labs flag POST /tenant and /tenant/checkout check too, so the screens never offer what the server refuses. */
export const SELF_SERVE_SIGNUP_FLAG = "selfServeSignup";

export const signupOpen = (status: SubscriptionStatus | null | undefined): boolean =>
  status?.features?.[SELF_SERVE_SIGNUP_FLAG] === true;

/** Anyone governed by an organization, as a member, admin or owner. */
export const belongsToOrg = (status: SubscriptionStatus | null | undefined): boolean =>
  isAdminTenantType(status?.tenantType);

/** Where an organization with no live licence pays: `signupStepFor` resumes the checkout step there. */
export const checkoutPath = (plan: string): string => `/?plan=${plan}`;

const paidPlan = (tenantType: string | null | undefined): tenantType is OrgPlan =>
  tenantType === "family" || tenantType === "business";

/**
 * ENG-3244, ENG-3181: whoever bills a family or business with no live licence, whose checkout signup resumes.
 * A business only before its first licence: checkout licenses one seat, so a lapsed one with a roster
 * would come back under-billed and with its members still deactivated.
 */
export const awaitingCheckout = (status: SubscriptionStatus | null | undefined): boolean =>
  !!status && paidPlan(status.tenantType) && holdsBilling(status.role, status.tenantType)
  && !(status.hasSubscription && status.status === "active")
  && (status.tenantType === "family" || status.status == null);

export function readSignupIntent(search: string): SignupIntent | null {
  const params = new URLSearchParams(search);
  // The legacy checkout return also carries `?plan=`, and means something else.
  if (params.has("payment")) return null;
  const source = params.get("source");
  const rawPlan = params.get("plan");
  const plan = rawPlan ? PLAN_ALIASES[rawPlan.trim().toLowerCase()] ?? null : null;
  if (!source && !plan) return null;
  return { source, plan };
}

export type SignupStep = "none" | "wait" | "plan" | "create" | "checkout" | "individual";

export interface SignupContext {
  consoleKind: ConsoleKind;
  status: SubscriptionStatus | null | undefined;
}

/**
 * Which signup screen, if any, a signed-in person sees. Only someone in no organization at all: an
 * org they owned would outrank their employer's for browser policy (ENG-3454 review), and from the
 * generic Marketplace listing an existing admin just wants their console. A failed status read shows
 * nothing rather than guess.
 */
export function signupStepFor(intent: SignupIntent | null, { consoleKind, status }: SignupContext): SignupStep {
  if (!intent) return "none";
  if (consoleKind === "pending") return "wait";
  if (!signupOpen(status)) return "none";
  if (intent.plan === status?.tenantType && awaitingCheckout(status)) return "checkout";
  if (belongsToOrg(status)) return "none";
  // After the gates, like every other step: "Just me" from a pricing or Marketplace link must not
  // send an org member or owner to the Web Store instead of their console, flag or no flag.
  if (intent.plan === "individual") return "individual";
  return intent.plan ? "create" : "plan";
}

/** The URL without the signup parameters, so a reload doesn't start over. */
export function withoutSignupParams(href: string): string {
  const url = new URL(href);
  url.searchParams.delete("source");
  url.searchParams.delete("plan");
  return url.pathname + url.search + url.hash;
}
